import { useEffect, useState } from 'react';
import { api } from '../api';
import { saveRequest } from '../ui/save-request';
import { PageHeader } from '../ui/PageHeader';
import { ErrorState, Loading } from '../ui/State';
import { useCapability } from '../ui/StaffSession';
import { Permissions as P } from '@myfm/domain';
import { formatDateOnly, formatDateTime } from '../dates';
import { Action, Select, Text, type Row, type Save } from './conveyancing-forms';
import { ResponsiveTable } from '../ui/ResponsiveTable';
export function ConveyancingSettings() {
  const [model, setModel] = useState<Row>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [refresh, setRefresh] = useState(0),
    manage = useCapability(P.ManageBusinessBilling),
    approve = useCapability(P.ApproveBusinessBilling);
  useEffect(() => {
    let active = true;
    api<Row>('/api/business-billing/settings')
      .then((m) => active && setModel(m))
      .catch((e) => active && setError(e));
    return () => {
      active = false;
    };
  }, [refresh]);
  const save: Save = async (body) => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await saveRequest<Row>('/api/business-billing/settings', { method: 'POST', body: JSON.stringify(body) });
      setMessage('Saved. Configuration history has been retained.');
      setRefresh((n) => n + 1);
      return r;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  if (!model) return error ? <ErrorState error={error} onRetry={() => setRefresh((n) => n + 1)} /> : <Loading />;
  const current = model.schedules.find((s: Row) => s.effective_from.slice(0, 10) <= new Date().toISOString().slice(0, 10)),
    profile = model.profiles.find((p: Row) => p.approved_at) ?? model.profiles[0];
  return (
    <div className="demand-workflow">
      <PageHeader
        title="Conveyancing fees and business billing"
        context="Configure the managing agent’s own service prices and business payment instructions. New versions preserve historical quotes and invoices."
      />
      {Boolean(error) && <ErrorState error={error} />} {message && <p role="status">{message}</p>}
      <section>
        <h2>Current fee schedule</h2>
        {current ? (
          <>
            <p>
              {current.title} · Effective {formatDateOnly(current.effective_from)}
            </p>
            <ResponsiveTable>
              <caption>Configured conveyancing fees</caption>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Net fee</th>
                  <th>VAT rate</th>
                  <th>Includes</th>
                </tr>
              </thead>
              <tbody>
                {current.items.map((i: Row) => (
                  <tr key={i.code}>
                    <td>{i.label}</td>
                    <td>£{i.net}</td>
                    <td>{i.vatRate}%</td>
                    <td>{i.includes.map((c: string) => current.items.find((f: Row) => f.code === c)?.label ?? c).join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </>
        ) : (
          <p>No currently effective tariff.</p>
        )}
      </section>
      {manage && <TariffForm key={current?.id ?? 'new'} initial={current} busy={busy} save={save} />}
      <section>
        <h2>Business issuer and payment details</h2>
        <p>
          The invoice issuer is the operating managing agent, not the building’s service-charge account. Payment instructions require a different authorised reviewer’s approval.
        </p>
        {model.profiles.map((p: Row) => (
          <details key={p.id}>
            <summary>
              {p.details.legalName} — {formatDateTime(p.created_at)} — {p.approved_at ? 'Approved' : 'Awaiting independent approval'}
            </summary>
            <p>
              {p.details.tradingName} · Company {p.details.companyNumber} · VAT {p.details.vatNumber || 'Not registered / not configured'}
            </p>
            <p>
              {p.details.address} · {p.details.contact}
            </p>
            <p>{p.details.paymentInstructions}</p>
            <p>Business payment source: {p.details.paymentSource}</p>
            {approve && !p.approved_at && (
              <Action
                title="Approve these business payment instructions"
                busy={busy}
                onSave={(v, key) => save({ action: 'Approve profile', profileId: p.id, reason: v.reason, idempotencyKey: key })}
              >
                <p>Independently verify the legal issuer and payment destination. You cannot approve a version you prepared.</p>
              </Action>
            )}
          </details>
        ))}
      </section>
      {manage && <IssuerForm key={profile?.id ?? 'profile'} model={model} initial={profile} busy={busy} save={save} />}
      <details>
        <summary>Fee schedule history</summary>
        {model.schedules.map((s: Row) => (
          <p key={s.id}>
            {s.title} · Effective {formatDateOnly(s.effective_from)} · Saved {formatDateTime(s.created_at)} · {s.reason}
          </p>
        ))}
      </details>
    </div>
  );
}
function TariffForm({ initial, busy, save }: { initial?: Row; busy: boolean; save: Save }) {
  const [items, setItems] = useState<Row[]>(initial?.items ?? []),
    [label, setLabel] = useState('');
  return (
    <Action
      title="Save a new fee schedule version"
      busy={busy}
      onSave={(v, key) => save({ action: 'Tariff', title: v.title, effectiveFrom: v.effectiveFrom, items, reason: v.reason, idempotencyKey: key })}
    >
      <Text name="title" label="Fee schedule title" value={initial?.title} />
      <Text name="effectiveFrom" label="Effective from" dateOnly />
      {items.map((item, n) => (
        <fieldset key={item.code}>
          <legend>{item.label}</legend>
          <label>
            Service description
            <input
              aria-label={'Description for ' + item.label}
              value={item.label}
              onChange={(e) => setItems((old) => old.map((x, i) => (i === n ? { ...x, label: e.target.value } : x)))}
              required
            />
          </label>
          <label>
            Net fee (£)
            <input
              aria-label={'Net fee for ' + item.label}
              value={item.net}
              onChange={(e) => setItems((old) => old.map((x, i) => (i === n ? { ...x, net: e.target.value } : x)))}
              inputMode="decimal"
              required
            />
          </label>
          <label>
            VAT rate (%)
            <input
              aria-label={'VAT rate for ' + item.label}
              value={item.vatRate}
              onChange={(e) => setItems((old) => old.map((x, i) => (i === n ? { ...x, vatRate: e.target.value } : x)))}
              inputMode="decimal"
              required
            />
          </label>
          <details>
            <summary>Services included in this price</summary>
            {items
              .filter((x) => x.code !== item.code)
              .map((other) => (
                <label key={other.code}>
                  <input
                    type="checkbox"
                    checked={item.includes.includes(other.code)}
                    onChange={(e) =>
                      setItems((old) =>
                        old.map((x, i) => (i === n ? { ...x, includes: e.target.checked ? [...x.includes, other.code] : x.includes.filter((c: string) => c !== other.code) } : x))
                      )
                    }
                  />
                  {other.label}
                </label>
              ))}
          </details>
          <button
            type="button"
            onClick={() => setItems((old) => old.filter((_, i) => i !== n).map((x) => ({ ...x, includes: x.includes.filter((c: string) => c !== item.code) })))}
          >
            Remove this service from the new version
          </button>
        </fieldset>
      ))}
      <label>
        New service description
        <input value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>
      <button
        type="button"
        disabled={label.trim().length < 3}
        onClick={() => {
          setItems((old) => [...old, { code: 'custom-' + crypto.randomUUID(), label: label.trim(), net: '', vatRate: '', includes: [] }]);
          setLabel('');
        }}
      >
        Add configurable service
      </button>
      <p>Enter all amounts and rates explicitly. Existing issued invoices keep their retained prices. Future-dated versions apply from their effective date.</p>
    </Action>
  );
}
function IssuerForm({ model, initial, busy, save }: { model: Row; initial?: Row; busy: boolean; save: Save }) {
  const [org, setOrg] = useState(initial?.organisation_id ?? (model.organisations.length === 1 ? model.organisations[0].id : '')),
    d = initial?.details,
    canonical = model.organisations.find((o: Row) => o.id === org);
  return (
    <Action
      title="Prepare a business issuer version"
      busy={busy}
      onSave={(v, key) =>
        save({
          action: 'Profile',
          organisationId: v.organisationId,
          evidenceBuilding: model.documents.find((x: Row) => x.id === v.evidenceDocumentId)?.development_id,
          documentIds: [v.evidenceDocumentId],
          details: {
            legalName: canonical?.legal_name,
            tradingName: v.tradingName,
            companyNumber: v.companyNumber,
            address: v.address,
            contact: v.contact,
            vatNumber: v.vatNumber || '',
            paymentInstructions: v.paymentInstructions,
            paymentSource: v.paymentSource,
            jurisdiction: v.jurisdiction
          },
          reason: v.reason,
          idempotencyKey: key
        })
      }
    >
      <Select
        name="organisationId"
        label="Operating managing-agent organisation"
        value={org}
        options={model.organisations.map((o: Row) => [o.id, o.legal_name])}
        onChange={setOrg}
      />
      <p>Legal invoice issuer: {canonical?.legal_name ?? 'Select the operating managing agent'}</p>
      <Text name="tradingName" label="Trading name shown on invoice" value={d?.tradingName ?? canonical?.trading_name} optional />
      <Text name="companyNumber" label="Registered company number" value={d?.companyNumber ?? canonical?.company_number} />
      <Text name="address" label="Business invoice address" value={d?.address} type="textarea" />
      <Text name="contact" label="Business contact details" value={d?.contact} />
      <Text name="vatNumber" label="VAT registration number, if registered" value={d?.vatNumber} optional />
      <Text name="paymentInstructions" label="Business payment instructions" value={d?.paymentInstructions} type="textarea" />
      <Text name="paymentSource" label="Business bank or payment-source identifier" value={d?.paymentSource} />
      <Select name="jurisdiction" label="Business jurisdiction" value={d?.jurisdiction} options={['England', 'Wales'].map((s) => [s, s])} />
      <Select name="evidenceDocumentId" label="Retained business payment instructions evidence" options={(model.documents ?? []).map((d: Row) => [d.id, d.title])} />
      <p>Use the managing agent’s own business account. Never enter a building client-money account. No bank details or registrations are invented by the system.</p>
    </Action>
  );
}
